import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';
import { useSelector, useDispatch } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router-dom';
import { useAuthProfile } from '../../app/hooks';
import { selectLastgetSearchByStoryStatusArgsAndData } from '../../AppBody';
import { getSearchByCoordinates } from '../../features/searchSlice';
import { setPublicPinClicked, setAnalyticUsersClicked } from '../../features/dataProcessingSlice';
import { activateMarker } from '../../features/activeMarkers';
import { GetUserTypePin } from '../MessageProfile';
import { FloatingContainerMorph } from './FloatingContainerMorph';

const PAGE_SIZE = 5;

interface FloatingSearchResultsProps {
  minimized?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
  fullWidth?: boolean;
}

/* ── Compact result row ── */
const ResultRow: React.FC<{ patient: any; authStatus: string | null }> = ({ patient, authStatus }) => {
  const dispatch = useDispatch<any>();
  const distanceInMiles = (patient.distance ?? 0) / 1609.34;
  const formattedDistance = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(distanceInMiles);

  const handleClick = () => {
    dispatch(getSearchByCoordinates({ lat: patient.lat, lng: patient.lng }));
    dispatch(setAnalyticUsersClicked(false));
    dispatch(activateMarker({ id: patient.id, disableHover: true }));
    dispatch(setPublicPinClicked(patient.id));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      onClick={handleClick}
      sx={{
        py: 0.75,
        px: 1,
        mx: -1,
        borderRadius: 1.5,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: 'rgba(101, 110, 255, 0.06)' },
      }}
    >
      {/* Pin type icon */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {GetUserTypePin(patient.type, { style: { height: 20, width: 20 } }).icon}
      </Box>

      {/* Name + location */}
      <Stack direction="column" sx={{ flex: 1, minWidth: 0 }} gap={0}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', lineHeight: 1.3 }}
        >
          {patient.name_public}
        </Typography>
        {patient.formattedsearchterm && (
          <Stack direction="row" alignItems="center" gap={0.25}>
            <PlaceIcon sx={{ fontSize: 11, color: '#bbb', flexShrink: 0 }} />
            <Typography variant="caption" noWrap sx={{ color: '#888', fontSize: 11, lineHeight: 1.2 }}>
              {patient.formattedsearchterm}
            </Typography>
          </Stack>
        )}
      </Stack>

      {/* Distance chip */}
      {authStatus === 'authenticated' && patient.distance != null && (
        <Chip
          size="small"
          label={`${formattedDistance} mi`}
          sx={{
            backgroundColor: 'rgba(101, 110, 255, 0.08)',
            color: '#656EFF',
            fontWeight: 600,
            fontSize: 10,
            height: 20,
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}
    </Stack>
  );
};

/* ── Paginated result list (shared between story & location) ── */
const ResultList: React.FC<{ data: any[]; authStatus: string | null }> = ({ data, authStatus }) => {
  const [page, setPage] = useState(0);
  const resultCount = data.length;
  const pageCount = Math.ceil(resultCount / PAGE_SIZE);
  const pageData = data.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  if (resultCount === 0) {
    return (
      <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 1 }}>
        No members found.
      </Typography>
    );
  }

  return (
    <Stack direction="column" gap={0}>
      {pageData.map((patient: any, i: number) => (
        <React.Fragment key={String(patient.id) + String(patient.data_variable ?? '')}>
          {i > 0 && <Divider sx={{ borderColor: 'rgba(164, 142, 233, 0.12)' }} />}
          <ResultRow patient={patient} authStatus={authStatus} />
        </React.Fragment>
      ))}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, v) => setPage(v - 1)}
            size="small"
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: 12,
                minWidth: 26,
                height: 26,
                color: '#656EFF',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(101, 110, 255, 0.12)',
                  fontWeight: 700,
                },
              },
            }}
          />
        </Box>
      )}
    </Stack>
  );
};

export const FloatingSearchResults: React.FC<FloatingSearchResultsProps> = ({
  minimized = false,
  onMinimize,
  onExpand,
  fullWidth = false,
}) => {
  const { authStatus } = useAuthProfile();
  const navigate = useNavigate();

  // Story search results
  const lastgetSearchByStoryStatusArgsAndData = useSelector((state: any) =>
    selectLastgetSearchByStoryStatusArgsAndData(state)
  );
  const storyStatus = lastgetSearchByStoryStatusArgsAndData?.status;
  const storyArgs = lastgetSearchByStoryStatusArgsAndData?.args;
  const storyData: any[] = lastgetSearchByStoryStatusArgsAndData?.data ?? [];
  const hasStorySearch = storyArgs && storyStatus !== undefined;

  // Location search results — TODO: wire up once data structure is confirmed
  const hasLocationSearch = false;
  const locationPins: any[] = [];

  // Don't render if no active search of either type
  if (!hasStorySearch && !hasLocationSearch) return null;

  const storyFulfilled = storyStatus === 'fulfilled';
  const storyPending = storyStatus === QueryStatus.pending;
  const storyRejected = storyStatus === QueryStatus.rejected;

  // Total results for title
  const storyCount = hasStorySearch && storyFulfilled ? storyData.length : 0;
  const locationCount = hasLocationSearch ? locationPins.length : 0;
  const totalCount = storyCount + locationCount;

  const titleText = storyPending
    ? 'Searching…'
    : totalCount > 0
    ? `${totalCount} Result${totalCount === 1 ? '' : 's'}`
    : 'Search Results';

  return (
    <FloatingContainerMorph
      minimized={minimized}
      onMinimize={onMinimize}
      onExpand={onExpand}
      icon={<SearchIcon sx={{ color: '#656EFF', fontSize: 20 }} />}
      title={titleText}
      sx={{ width: fullWidth ? '100%' : undefined }}
    >
      <Box sx={{ px: 2, pb: 1.5 }}>
        {/* Loading (story search pending) */}
        {storyPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ color: '#656EFF' }} />
          </Box>
        )}

        {/* Error (story search rejected) */}
        {storyRejected && (
          <Alert severity="error" sx={{ borderRadius: 2, fontSize: 12 }}>
            An error occurred while retrieving results. Please try again.
          </Alert>
        )}

        {/* Story search results */}
        {hasStorySearch && storyFulfilled && storyData.length > 0 && (
          <>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: '#888', fontStyle: 'italic', display: 'block', mb: 0.5, lineHeight: 1.2 }}
            >
              Story: "{storyArgs.story}"
            </Typography>
            <ResultList data={storyData} authStatus={authStatus} />
          </>
        )}

        {/* Divider between sections when both are present */}
        {hasStorySearch && storyFulfilled && storyData.length > 0 && hasLocationSearch && (
          <Divider sx={{ my: 1, borderColor: 'rgba(164, 142, 233, 0.25)' }} />
        )}

        {/* Location search results */}
        {hasLocationSearch && (
          <>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: '#888', fontStyle: 'italic', display: 'block', mb: 0.5, lineHeight: 1.2 }}
            >
              Location: {locationPins.length} member{locationPins.length === 1 ? '' : 's'} nearby
            </Typography>
            <ResultList data={locationPins} authStatus={authStatus} />
          </>
        )}

        {/* No results for either */}
        {!storyPending && totalCount === 0 && !storyRejected && (
          <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 1 }}>
            No members found.
          </Typography>
        )}

        {/* Sign-in nudge */}
        {authStatus === 'unauthenticated' && totalCount > 0 && (
          <Link
            variant="caption"
            onClick={() => navigate('/signin')}
            sx={{ cursor: 'pointer', display: 'block', textAlign: 'center', mt: 1, color: '#656EFF' }}
          >
            Sign in for more results
          </Link>
        )}
      </Box>
    </FloatingContainerMorph>
  );
};
